import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Play, Clock, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/dashboard';
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Sesión iniciada correctamente');
      navigate(redirectTo);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerData.name, registerData.email, registerData.password);
      toast.success('Cuenta creada correctamente');
      navigate(redirectTo);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cuenta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0c1829] flex-col justify-between p-12">
        <div>
          <div className="flex items-center gap-3 mb-16">
            <img src="/logo-enubes.webp" alt="eNubes" className="h-8" />
            <span className="text-zinc-500 mx-1">|</span>
            <span className="font-heading text-2xl font-semibold tracking-tight text-white">EventRun</span>
          </div>
          
          <div className="space-y-8">
            <h1 className="font-heading text-5xl font-extrabold tracking-tight text-white leading-tight">
              Control total de tus eventos en vivo
            </h1>
            <p className="text-lg text-zinc-400 max-w-md leading-relaxed">
              Gestiona el rundown de tu evento con precisión profesional. 
              Control de tiempo, fases y colaboración en tiempo real.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="glass rounded-lg p-4">
            <Clock className="w-6 h-6 text-white mb-3" />
            <p className="text-sm font-medium text-white">Control de tiempo</p>
            <p className="text-xs text-zinc-400">Precisión al segundo</p>
          </div>
          <div className="glass rounded-lg p-4">
            <Users className="w-6 h-6 text-white mb-3" />
            <p className="text-sm font-medium text-white">Colaboración</p>
            <p className="text-xs text-zinc-400">Tiempo real</p>
          </div>
          <div className="glass rounded-lg p-4">
            <Play className="w-6 h-6 text-white mb-3" />
            <p className="text-sm font-medium text-white">Drag & Drop</p>
            <p className="text-xs text-zinc-400">Reordena fácil</p>
          </div>
        </div>
      </div>

      {/* Right side - Auth form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src="/logo-enubes.webp" alt="eNubes" className="h-7 dark:invert-0 invert" />
            <span className="text-muted-foreground mx-1">|</span>
            <span className="font-heading text-2xl font-semibold tracking-tight">EventRun</span>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Iniciar sesión</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Crear cuenta</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Bienvenido de nuevo</CardTitle>
                  <CardDescription>Ingresa tus credenciales para continuar</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="tu@email.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Contraseña</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        data-testid="login-password-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                      {loading ? 'Ingresando...' : 'Iniciar sesión'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Crear cuenta</CardTitle>
                  <CardDescription>Regístrate para comenzar a usar EventRun</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Nombre</Label>
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="Tu nombre"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="tu@email.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Contraseña</Label>
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        data-testid="register-password-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
                      {loading ? 'Creando cuenta...' : 'Crear cuenta'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
